




































































































































































































































import CAvatar from '~/components/shared/configurable/image/avatar/CAvatar.vue'
import { computed, defineComponent, ref, toRefs } from '@nuxtjs/composition-api'
import { Ref } from '@vue/composition-api'
import { useClassifiedForm } from '~/compositions/form'
import { formFieldProps } from '~/constants/form'
import FieldError from '~/components/car/classifieds/form/fieldsets/fields/FieldError.vue'
import CConfirmationModal from '~/components/shared/configurable/modal/CConfirmationModal.vue'
import { ciTimes } from '~/icons/source/regular/times'
import CCustomSelect from '~/components/shared/configurable/form/select/custom/CCustomSelect.vue'
import { Option } from '~/models/shared/types'
import { range } from '~/utils/array'
import { useI18n } from '~/compositions/i18n'
import { mapBackendOptionToOption } from '~/utils/form'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_FORM_NS,
  FormState
} from '~/store/modules/shared/classifieds/form/state'
import { useDep } from '~/compositions/dependency-container'
import { LegacyUrlService } from '~/services/legacy/url/LegacyUrlService'
import CYesOrNoButtonGroup from '~/components/shared/configurable/form/checkbox/CYesOrNoButtonGroup.vue'
import { defineComponentTranslations } from '~/utils/i18n'

export default defineComponent({
  props: formFieldProps,
  components: {
    CYesOrNoButtonGroup,
    CCustomSelect,
    CConfirmationModal,
    FieldError,
    CAvatar
  },
  setup(props) {
    const { specs, name, fieldError } = toRefs(props)
    const { state } = useNamespacedStore<FormState>(CLASSIFIED_FORM_NS)
    const legacyUrlService = useDep(LegacyUrlService)

    const financeEnabled = ref<boolean>(false)

    const capcodes = ref<number[] | null>(null)
    const capcodesError = ref<string | null>(null)

    const selectedYear = ref<number | null>(
      specs.value.registration.suggestions.year || null
    )
    const yearError = ref<string | null>(null)

    const selectedMonth = ref<number | null>(
      specs.value.registration.suggestions.month || null
    )
    const monthError = ref<string | null>(null)

    const mileage = ref<number | null>(specs.value.mileage.suggestions || null)
    const mileageError = ref<string | null>(null)

    const licencePlate = ref<number | null>(null)
    const licencePlateError = ref<string | null>(null)

    const vin = ref<number | null>(null)
    const vinError = ref<string | null>(null)

    const bankingProduct = ref<number | null>(null)
    const bankingProductError = ref<string | null>(null)

    const onlineFinanceError = ref<string | null>(
      typeof fieldError.value === 'string'
        ? fieldError.value
        : fieldError.value?.online_finance || null
    )

    const { t } = useI18n()

    const loading = computed(() => state.schemaLoading)

    const selectedMake = computed(() => state.fieldValues.makemodel?.make)
    const selectedModel = computed(() => state.fieldValues.makemodel?.model)
    const makeModelSelected = computed(() =>
      Boolean(selectedMake.value && selectedModel.value)
    )

    function onValue(v: Ref<any>) {
      financeEnabled.value = Boolean(v.value?.accepts_online_financing)
      selectedYear.value = v.value?.registration?.year
      selectedMonth.value = v.value?.registration?.month
      mileage.value = v.value?.mileage
      vin.value = v.value?.vin
      licencePlate.value = v.value?.licence_plate
      bankingProduct.value = v.value?.banking_product?.ProductId
      capcodes.value = v.value?.capcodes?.join(',') || null
    }

    function onError(e: Ref<any>) {
      onlineFinanceError.value = e.value || null
      yearError.value = e.value?.registration?.year
      monthError.value = e.value?.registration?.month
      mileageError.value = e.value?.mileage
      bankingProductError.value = e.value?.banking_product
      capcodesError.value = e.value?.capcodes
    }

    function onSpecs(s: Ref<any>) {
      if (!financeEnabled.value) {
        return
      }
      if (!selectedMonth.value && s.value.registration.suggestions.month) {
        selectedMonth.value = s.value.registration.suggestions.month
      }
      if (!selectedYear.value && s.value.registration.suggestions.year) {
        selectedYear.value = s.value.registration.suggestions.year
      }
      if (!mileage.value && s.value.mileage.suggestions) {
        mileage.value = s.value.mileage.suggestions
      }
    }

    const { debouncedValueChanged } = useClassifiedForm(props, {
      onValue,
      onError,
      onSpecs
    })

    const handleFinanceEnabledChange = (accepts: boolean) => {
      financeEnabled.value = accepts
      if (!accepts) {
        // reset values here?
        capcodes.value = null
        selectedMonth.value = null
        selectedYear.value = null
        mileage.value = null
        bankingProduct.value = null
      } else {
        onSpecs(specs) // updated specs
      }
      emitValues()
    }

    const handleCapcodeChange = (_capcodes: string) => {
      bankingProduct.value = null
      emitValues()
    }

    const capcodesOptions = computed(() => {
      const arr = []
      if (
        !specs.value.capcodes?.suggestions ||
        !specs.value.capcodes?.values ||
        specs.value.capcodes?.values.length === 0
      ) {
        return arr
      }

      if (specs.value.capcodes.suggestions.length) {
        arr.push({
          label: t('suggested_versions'),
          options: specs.value.capcodes.suggestions.map((o: any) => ({
            text: o.label,
            value: o.value.join(',')
          }))
        })
      }

      arr.push(
        ...specs.value.capcodes.values.map((o: any) => ({
          text: o.label,
          value: o.value.join(',')
        }))
      )
      return arr
    })

    const noResultsForThisMake = computed(() => {
      return Boolean(
        capcodesOptions.value.length === 0 &&
          makeModelSelected.value &&
          !loading.value
      )
    })

    const bankingProductOptions = computed(() => {
      if (!specs.value.banking_product?.values) {
        return []
      }
      return specs.value.banking_product.values.map((o: any) => ({
        text: o.label,
        value: o.value
      }))
    })

    const yearsOptions: Ref<Option[]> = computed(() => [
      { text: t('').toString(), value: null },
      ...range(
        specs.value.registration.values.years.min,
        specs.value.registration.values.years.max
      )
        .reverse()
        .map(y => ({ text: y.toString(), value: y }))
    ])

    const monthsOptions = computed(() => {
      if (!specs.value.registration?.values?.months) {
        return []
      }

      return specs.value.registration.values.months.map(
        mapBackendOptionToOption
      )
    })

    const emitValues = () => {
      if (!financeEnabled.value) {
        debouncedValueChanged(name.value, {
          accepts_online_financing: false
        })
        return
      }
      debouncedValueChanged(name.value, {
        accepts_online_financing: true,
        capcodes: capcodes.value?.split(',').map(y => Number(y)) || undefined,
        mileage: mileage.value ? Number(mileage.value) : undefined,
        registration: {
          year: selectedYear.value,
          month: selectedMonth.value
        },
        licence_plate: licencePlate.value,
        vin: vin.value,
        banking_product: bankingProduct.value || undefined
      })
    }

    return {
      ciTimes,
      financeEnabled,
      capcodes,
      capcodesError,
      capcodesOptions,
      selectedMonth,
      selectedYear,
      yearError,
      monthError,
      yearsOptions,
      monthsOptions,
      mileage,
      mileageError,
      bankingProductOptions,
      bankingProduct,
      bankingProductError,
      onlineFinanceError,
      makeModelSelected,
      noResultsForThisMake,
      legacyUrlService,
      loading,
      licencePlate,
      licencePlateError,
      vin,
      vinError,
      state,
      handleFinanceEnabledChange,
      emitValues,
      handleCapcodeChange,
      t
    }
  },
  i18n: defineComponentTranslations({
    do_you_want_online_finance: {
      en: 'Do you want to enable financing through Santander?',
      el: 'Επιθυμείς χρηματοδότηση μέσω Santander;'
    },
    mileage_placeholder: {
      en: 'Number of km',
      el: 'Αριθμός χιλιομέτρων'
    },
    licence_plate_placeholder: {
      en: 'e.g. ΖΗΡ 12...',
      el: 'π.χ. ΖΗΡ 12...'
    },
    data_only_for_santander: {
      en:
        'The following fields are only used by Santander and is not visible to other users',
      el:
        'Τα παρακάτω στοιχεία χρησιμοποιούνται μόνο από την Santander και δεν είναι ορατά στους χρήστες'
    }
  })
})
